import React from 'react'
import { Observable } from 'rx'
import { observeImmutable } from '@core/Components/HOCs'
import { NoticeBox } from '@core/Components/NoticeBox'
import { state$ as quickbetState$, QuickbetState } from '@core/Areas/Quickbet/driver'
import {
  state$ as userAccountState$,
  UserAccountState,
} from '@core/State/UserAccount/userAccountDriver'
import { state$ as formulaState$, FormulaState } from '../Formula/driver'
import { state$ as investmentState$, InvestmentState } from '../BetInvestment/driver'
import { calculateCombinations as favouriteNumbersCombinations } from '@core/Areas/FavouriteNumbers/calculator'
import { calculateCombinations as tippingCombinations } from '@core/Areas/Tipping/helpers/calculator'
import { NoticeBoxTypes } from '@core/Components/NoticeBox/types'
import { Money } from '@core/Components/Text/Money'
import { networkErrorMessage } from '@core/Data/betting'
import { CustomerServiceTelephone } from '@mobi/component-library/Base/Link'
import { BonusDetailsNotification } from './BonusDetailsNotification'
import { DisclaimerStyled } from './NotificationTypes.styles'
import { toMoney } from '@mobi/utils/money'
import { calculateBetCost } from '../../helpers/calculator'
import { ToteSelection } from '@mobi/betslip/types'
import { isAllUpSelection, isToteSelection } from '@mobi/betslip/helpers/typeGuards'
import { isFavouriteNumbersSelection, isToteSportsSelection } from '@core/Data/Betting/selections'

/**
 * Main Notifications Component
 */
const notificationState$ = Observable.combineLatest(
  quickbetState$,
  userAccountState$,
  formulaState$,
  investmentState$,
  (quickbetRecord, userAccountRecord, formulaRecord, investmentRecord) => {
    const investmentState = investmentRecord.toJS() as InvestmentState
    return {
      accountBalance: userAccountRecord.accountBalance,
      canBet: quickbetRecord.canBet,
      bettingType: quickbetRecord.bettingType,
      notificationType: quickbetRecord.notificationType,
      notificationTitle: quickbetRecord.notificationTitle,
      notificationSubtitle: quickbetRecord.notificationSubtitle,
      isEachWay: quickbetRecord.isEachWay,
      selection: (quickbetRecord.toJS() as QuickbetState).selection,
      numberOfCombinationsSelected: formulaRecord.numberOfCombinationsSelected,
      winInvestment: investmentState.win.value,
      placeInvestment: investmentState.place.value,
      isUsingBonusBet: quickbetRecord.isUsingBonusBet,
      isUsingBonusCash: quickbetRecord.isUsingBonusCash,
      bonusCashBalance: userAccountRecord.bonusCashBalance,
    }
  }
)

export const Notifications: React.ComponentClass = observeImmutable(
  notificationState$,
  ({ record }) => <NotificationsComponent {...record} />
)

Notifications.displayName = 'Notifications'

// Only Exported for Unit Test
export const NotificationsComponent = ({
  accountBalance,
  canBet,
  bettingType,
  notificationType,
  notificationTitle,
  notificationSubtitle,
  numberOfCombinationsSelected,
  selection,
  isEachWay,
  winInvestment,
  placeInvestment,
  isUsingBonusBet,
  isUsingBonusCash,
  bonusCashBalance,
}: NotificationsProps) => {
  if (!notificationType) {
    return null
  }

  if (notificationType === 'Unauthorized') {
    return <NotificationBox icon='Error' title='Please log in to place bet' />
  }

  if (notificationType === 'InvalidSuperPickSelection') {
    return (
      <NotificationBox
        icon='Warning'
        title='Invalid SuperPick selection'
        subtitle={notificationSubtitle == null ? undefined : notificationSubtitle}
      />
    )
  }

  if (notificationType === 'HandledWarning') {
    return (
      <NotificationBox
        icon='Warning'
        title={notificationTitle || 'Please edit your bet to continue'}
        subtitle={notificationSubtitle || undefined}
      />
    )
  }

  if (notificationType === 'InsufficientFunds') {
    return (
      <NotificationBox icon='Error' title='Your account has insufficient funds'>
        {accountBalance ? (
          <p key='insufficientFunds'>
            Current account balance is{' '}
            <strong>
              <Money amount={accountBalance} />
            </strong>
          </p>
        ) : undefined}
      </NotificationBox>
    )
  }

  if (notificationType === 'NonHandledError') {
    return (
      <NotificationBox
        icon='Error'
        title={notificationTitle as string}
        subtitle={
          notificationTitle === networkErrorMessage
            ? canBet
              ? 'Please check your internet connection and try again.'
              : 'Please check your internet connection.'
            : undefined
        }
      >
        {notificationTitle === networkErrorMessage && !canBet ? (
          <p key='unHandledError'>
            <strong>IMPORTANT </strong>Please check your Transactions or contact our Customer
            Service Centre on <CustomerServiceTelephone /> to confirm whether the operation
            succeeded.
          </p>
        ) : undefined}
      </NotificationBox>
    )
  }

  const numberOfCombinations = isAllUpSelection(selection)
    ? numberOfCombinationsSelected
    : isToteSelection(selection)
      ? selection.numberOfCombinations
      : isFavouriteNumbersSelection(selection)
        ? favouriteNumbersCombinations(selection.bets)
        : isToteSportsSelection(selection)
          ? tippingCombinations(selection.betSelections)
          : 1

  const betType = bettingType === 'tote-racing' ? (selection as ToteSelection).betType : undefined
  const betCost = calculateBetCost(
    bettingType,
    winInvestment,
    placeInvestment,
    isEachWay,
    betType,
    numberOfCombinations
  )

  if (notificationType === 'ProposeSucceeded') {
    return (
      <NotificationBox icon='Info' title={`This bet will cost ${toMoney(betCost)}`}>
        <>
          <BonusDetailsNotification
            betCost={betCost}
            isUsingBonusBet={isUsingBonusBet}
            isUsingBonusCash={isUsingBonusCash}
            bonusCashBalance={bonusCashBalance}
            accountBalance={accountBalance}
          />
          <DisclaimerStyled>Bets cannot be cancelled once placed.</DisclaimerStyled>
        </>
      </NotificationBox>
    )
  }

  if (notificationType === 'PriceChange') {
    return (
      <NotificationBox
        icon='Warning'
        title='Prices have changed'
        subtitle={`This bet will cost ${toMoney(betCost)}`}
      />
    )
  }

  if (notificationType === 'HandicapChanged') {
    return <NotificationBox icon='Error' title='Handicap changed. Bet is no longer valid' />
  }

  if (notificationType === 'InsufficientInvestmentForCombinations') {
    return (
      <NotificationBox
        icon='Warning'
        title={`Insufficient investment for ${numberOfCombinations} combinations.`}
      />
    )
  }

  if (notificationType === 'BettingClosed') {
    return <NotificationBox icon='Error' title='Betting Closed' />
  }

  if (notificationType === 'MysteryQuickPickFixedInvestment') {
    return <NotificationBox icon='Info' title='Investment is pre-set for this bet type' />
  }

  if (notificationType === 'InvalidBonusBet') {
    return <NotificationBox icon='Error' title='Bonus Bet has already been used' />
  }

  return null
}

// ===============
// Local Component
// ===============

const NotificationBox: React.FC<{
  title: string
  icon: NoticeBoxTypes
  subtitle?: string
  children?: React.ReactNode
}> = ({ title, subtitle, icon, children }) => (
  <NoticeBox title={title} subtitle={subtitle} noticeBoxType={icon} hasBorder>
    {children}
  </NoticeBox>
)

// =====
// Types
// =====

type NotificationsProps = Pick<UserAccountState, 'accountBalance' | 'bonusCashBalance'> &
  Pick<
    QuickbetState,
    | 'canBet'
    | 'notificationType'
    | 'notificationTitle'
    | 'notificationSubtitle'
    | 'bettingType'
    | 'selection'
    | 'isEachWay'
    | 'isUsingBonusBet'
    | 'isUsingBonusCash'
  > &
  Pick<FormulaState, 'numberOfCombinationsSelected'> & {
    winInvestment: number
    placeInvestment: number
  }
